import { advance, dateOf, earn, hourOf, message, progress, spend, weekday, type LifeEvent, type LifeState, money } from './life.ts';
import { Rng } from './rng.ts';
import { hereCountry, localPrice, nudge } from './character.ts';
import { ageOf } from './lifecycle.ts';
import type { Country } from './travel.ts';

/**
 * Places with rules of their own: the bank (a savings account that pays interest, a loan that has
 * to be paid back every month) and the church (Sunday service needs a keyboardist, and a good one
 * gets paid and gets known). The game takes you there; this decides what it means.
 */

const DAY = 1440;
const naira = (s: LifeState, n: number) => money(s, n);
const clamp = (v: number) => Math.max(0, Math.min(100, v));

export interface BankState {
  savings: number;
  /** a loan being paid back monthly on the 1st */
  loan: { principal: number; monthly: number; left: number; missed: number; purpose: string } | null;
  /** 0..100: how the bank sees you (paying on time raises it, missing drops it) */
  credit: number;
  opened: boolean;
}

export const SAVINGS_RATE_YEARLY = 0.08;
export const LOAN_RATE_YEARLY = 0.28;

export function bankOf(s: LifeState): BankState {
  s.bank ??= { savings: 0, loan: null, credit: 50, opened: false };
  return s.bank;
}

/** Banks open weekdays 8:00–16:00. */
export function bankOpen(s: LifeState): boolean {
  const h = hourOf(s.minutes), wd = weekday(s.minutes);
  return wd < 5 && h >= 8 && h < 16;
}

export function openAccount(s: LifeState): LifeEvent[] {
  const b = bankOf(s);
  if (b.opened) return [];
  b.opened = true;
  s.history.push({ t: s.minutes, text: 'Opened a savings account.' });
  return [{ t: s.minutes, kind: 'info', text: 'Savings account open. It pays interest on the 1st of every month.' }];
}

export function deposit(s: LifeState, amount: number): boolean {
  const b = bankOf(s);
  if (!b.opened || amount <= 0 || !spend(s, amount, 'To savings')) return false;
  b.savings += amount;
  nudge(s, 'disciplined', 0.01);
  return true;
}

export function withdraw(s: LifeState, amount: number): boolean {
  const b = bankOf(s);
  if (!b.opened || amount <= 0 || amount > b.savings) return false;
  b.savings -= amount;
  earn(s, amount, 'From savings');
  return true;
}

/** What the bank would lend you, and on what terms (or why not). */
export function loanOffer(s: LifeState): { amount: number; months: number; monthly: number; reason?: string } {
  const b = bankOf(s);
  if (b.loan) return { amount: 0, months: 0, monthly: 0, reason: 'You already have a loan with us.' };
  const income = s.job?.incomeMonthly ?? 0;
  const biz = s.business ? s.business.week.reduce((a, x) => a + x, 0) / Math.max(1, s.business.week.length) * 26 : 0;
  const earning = Math.max(income, biz);
  if (earning <= 0) return { amount: 0, months: 0, monthly: 0, reason: 'We need to see an income: a job, or a business with takings.' };
  if (b.credit < 30) return { amount: 0, months: 0, monthly: 0, reason: 'Your record with us isn\'t good enough right now.' };
  const amount = Math.round((earning * (2 + b.credit / 25)) / 10_000) * 10_000;
  const months = 12;
  const r = LOAN_RATE_YEARLY / 12;
  const monthly = Math.round((amount * r) / (1 - Math.pow(1 + r, -months)) / 100) * 100;
  return { amount, months, monthly };
}

export function takeLoan(s: LifeState, purpose: string): LifeEvent[] | null {
  const o = loanOffer(s);
  if (o.reason || !o.amount) return null;
  const b = bankOf(s);
  b.loan = { principal: o.amount, monthly: o.monthly, left: o.months, missed: 0, purpose };
  earn(s, o.amount, 'Bank loan');
  s.history.push({ t: s.minutes, text: `Took a ${naira(s, o.amount)} loan from the bank${purpose ? ` for ${purpose}` : ''}.` });
  nudge(s, 'ambitious', 0.03);
  return [{ t: s.minutes, kind: 'pay', text: `${naira(s, o.amount)} loan approved. ${naira(s, o.monthly)} comes out on the 1st, for ${o.months} months.`, amount: o.amount }];
}

/** Called once a day: interest and repayments on the 1st. */
export function bankDay(s: LifeState, events: LifeEvent[]) {
  const b = s.bank;
  if (!b || dateOf(s.minutes).getUTCDate() !== 1) return;
  if (b.savings > 0) {
    const interest = Math.round(b.savings * SAVINGS_RATE_YEARLY / 12);
    if (interest > 0) { b.savings += interest; events.push({ t: s.minutes, kind: 'pay', text: `Savings interest: ${naira(s, interest)}.`, amount: interest }); }
  }
  const l = b.loan;
  if (!l) return;
  // repayments come from your account, then from savings; short means missed
  let paid = 0;
  if (s.money >= l.monthly) { spend(s, l.monthly, 'Loan repayment'); paid = l.monthly; }
  else if (s.money + b.savings >= l.monthly) { const from = l.monthly - s.money; b.savings -= from; spend(s, s.money, 'Loan repayment'); paid = l.monthly; }
  if (paid) {
    l.left--;
    b.credit = clamp(b.credit + 3);
    if (l.left <= 0) {
      b.loan = null;
      b.credit = clamp(b.credit + 10);
      s.history.push({ t: s.minutes, text: 'Paid off the bank loan.' });
      events.push({ t: s.minutes, kind: 'pay', text: 'Last loan repayment. You owe the bank nothing.' });
    } else events.push({ t: s.minutes, kind: 'rent', text: `Loan repayment: ${naira(s, l.monthly)} (${l.left} to go).`, amount: -l.monthly });
    return;
  }
  l.missed++;
  b.credit = clamp(b.credit - 15);
  // a missed month: penalty interest, and the calls start
  l.monthly = Math.round(l.monthly * 1.05 / 100) * 100;
  message(s, 'Bank', l.missed >= 3 ? 'Your loan is in default. The matter has been passed to our recovery team.' : `Your loan repayment of ${naira(s, l.monthly)} failed. Please fund your account.`, events);
  if (l.missed >= 3) {
    // recovery: they take what's there
    const took = Math.min(s.money + b.savings, l.monthly * l.left);
    b.savings = Math.max(0, b.savings - Math.max(0, took - s.money));
    spend(s, Math.min(s.money, took), 'Loan recovery');
    s.history.push({ t: s.minutes, text: 'Defaulted on a bank loan.' });
    b.loan = null;
  }
}

// --- mosque --------------------------------------------------------------------------------------

/** The five daily prayers (October, in each country's main city; hours, local). */
const PRAYER_TIMES: Record<Country, [string, number][]> = {
  nigeria: [['Fajr', 5.25], ['Dhuhr', 12.83], ['Asr', 16.08], ['Maghrib', 18.75], ['Isha', 19.92]],
  france: [['Fajr', 6.83], ['Dhuhr', 13.75], ['Asr', 16.75], ['Maghrib', 19.17], ['Isha', 20.67]],
  uae: [['Fajr', 4.83], ['Dhuhr', 12.3], ['Asr', 15.67], ['Maghrib', 18.1], ['Isha', 19.4]],
  italy: [['Fajr', 6.17], ['Dhuhr', 13.25], ['Asr', 16.33], ['Maghrib', 18.83], ['Isha', 20.25]],
  usa: [['Fajr', 5.83], ['Dhuhr', 12.83], ['Asr', 15.92], ['Maghrib', 18.42], ['Isha', 19.75]],
  china: [['Fajr', 4.75], ['Dhuhr', 11.83], ['Asr', 15.17], ['Maghrib', 17.5], ['Isha', 18.75]],
  japan: [['Fajr', 4.5], ['Dhuhr', 11.58], ['Asr', 14.83], ['Maghrib', 17.25], ['Isha', 18.58]],
};
const hhmm = (h: number) => `${Math.floor(h)}:${String(Math.round((h % 1) * 60)).padStart(2, '0')}`;

/** A prayer in congregation now (from the call to prayer for 45 minutes; Friday noon is Jumu'ah, longer). */
export function prayerNow(s: LifeState): { name: string; jumuah: boolean } | null {
  const h = hourOf(s.minutes), friday = weekday(s.minutes) === 4;
  for (const [name, t] of PRAYER_TIMES[hereCountry(s)]) {
    const jumuah = friday && name === 'Dhuhr';
    if (h >= t - (jumuah ? 0.4 : 0) && h < t + (jumuah ? 1.2 : 0.75)) return { name: jumuah ? "Jumu'ah" : name, jumuah };
  }
  return null;
}

/** The next prayer, in words ("Asr at 16:05"). */
export function nextPrayer(s: LifeState): string {
  const h = hourOf(s.minutes), times = PRAYER_TIMES[hereCountry(s)];
  const [name, t] = times.find(([, x]) => x > h) ?? times[0];
  return `${weekday(s.minutes) === 4 && name === 'Dhuhr' ? "Jumu'ah" : name} at ${hhmm(t)}`;
}

/**
 * You prayed (with the congregation, or alone): a quiet that settles you, the people around you (Jumu'ah:
 * greetings at the door afterwards), and praying on time is a discipline. `performance`: how closely you
 * followed (0..1).
 */
export function pray(s: LifeState, performance: number, congregation: { name: string; jumuah: boolean } | null): { lines: string[]; events: LifeEvent[] } {
  const events = advance(s, congregation?.jumuah ? 55 : 15);
  s.needs.social = clamp(s.needs.social + (congregation?.jumuah ? 18 : congregation ? 6 : 1));
  s.needs.fun = clamp(s.needs.fun + 4);
  s.stats.prayers = (s.stats.prayers ?? 0) + 1;
  nudge(s, 'disciplined', 0.003, events);
  const lines: string[] = [];
  if (congregation?.jumuah) lines.push('The khutbah, then the prayer. Outside, people shake hands and ask after each other\'s families.');
  else if (congregation) lines.push(`${congregation.name}, with the congregation.`);
  else lines.push('On your own, in a corner of the hall.');
  if (performance < 0.5) lines.push('You followed the person next to you more than the imam. Nobody minds.');
  const today = Math.floor(s.minutes / 1440);
  if (!s.history.some((x) => Math.floor(x.t / 1440) === today && /^Prayed/.test(x.text))) s.history.push({ t: s.minutes, text: congregation?.jumuah ? "Prayed Jumu'ah at the mosque." : 'Prayed at the mosque.' });
  return { lines, events };
}

// --- the club ------------------------------------------------------------------------------------

/** Open from 22:00 till 04:00. */
export function clubOpen(s: LifeState): boolean { const h = hourOf(s.minutes); return h >= 22 || h < 4; }
/** How full tonight is: Friday and Saturday nights are packed, Thursday's busy, the rest quiet (after midnight it's still last night). */
export function clubNight(s: LifeState): 'packed' | 'busy' | 'quiet' {
  const d = weekday(s.minutes - 4 * 60);
  return d === 4 || d === 5 ? 'packed' : d === 3 ? 'busy' : 'quiet';
}
const nightOf = (s: LifeState) => Math.floor((s.minutes - 4 * 60) / 1440);
/** Tonight at the club (drinks had, the table taken): starts fresh every night. */
function tonight(s: LifeState) {
  if (!s.club || s.club.night !== nightOf(s)) s.club = { night: nightOf(s), drinks: 0, vip: false, danced: 0, sprayed: 0, bottles: 0 };
  return s.club;
}
export const clubTonight = (s: LifeState) => ({ ...tonight(s) });

export function clubCover(s: LifeState): number { return localPrice(s, clubNight(s) === 'packed' ? 15_000 : 10_000, 500); }

/** The door: ID (18+), then the cover. */
export function enterClub(s: LifeState): { ok: boolean; line: string } {
  if (ageOf(s) < 18) return { ok: false, line: '"ID?" The bouncer reads it twice. "Come back when you\'re eighteen."' };
  if (!clubOpen(s)) return { ok: false, line: '"We open at ten." The door stays shut.' };
  const fee = clubCover(s);
  if (!spend(s, fee, 'Club entry')) return { ok: false, line: `"${naira(s, fee)} to get in." You don't have it.` };
  tonight(s);
  s.stats.clubNights = (s.stats.clubNights ?? 0) + 1;
  const n = clubNight(s);
  return { ok: true, line: n === 'packed' ? 'The bass hits you in the chest before the door\'s even closed. It\'s packed.' : n === 'busy' ? 'A good crowd tonight. The DJ is warming up.' : 'A quiet night: a few people at the bar, the floor mostly yours.' };
}

export const DRINKS = {
  water: { name: 'Water', price: 500 },
  beer: { name: 'A beer', price: 2_500 },
  cocktail: { name: 'A cocktail', price: 8_000 },
} as const;
export type DrinkKind = keyof typeof DRINKS;
export const drinkPrice = (s: LifeState, k: DrinkKind) => localPrice(s, DRINKS[k].price, k === 'water' ? 100 : 500);

/** A drink at the bar. After five, the barman pours you water. */
export function drinkAt(s: LifeState, k: DrinkKind): { ok: boolean; line: string; events: LifeEvent[] } {
  const t = tonight(s);
  if (k !== 'water' && t.drinks >= 5) return { ok: false, line: 'The barman slides a glass of water across instead. "On the house. Take it easy."', events: [] };
  const price = drinkPrice(s, k);
  if (!spend(s, price, DRINKS[k].name)) return { ok: false, line: `${naira(s, price)}. You don't have it.`, events: [] };
  const events = advance(s, 10);
  if (k === 'water') { s.needs.energy = clamp(s.needs.energy + 2); return { ok: true, line: 'Cold water. Good call.', events }; }
  t.drinks++;
  s.needs.fun = clamp(s.needs.fun + (k === 'beer' ? 5 : 8));
  s.needs.social = clamp(s.needs.social + 2);
  s.needs.energy = clamp(s.needs.energy - 2);
  return { ok: true, line: t.drinks >= 4 ? 'You\'re feeling it now.' : k === 'beer' ? 'Ice cold.' : 'Sweet, strong, and gone too fast.', events };
}

/**
 * You danced (`performance` 0..1 from the floor). Fun, a little fitness, and on a good night someone dances
 * with you and you swap numbers (`partner` is who: the game names them).
 */
export function danced(s: LifeState, performance: number, partner: string | null, place: string): { lines: string[]; events: LifeEvent[]; met: string | null } {
  const p = Math.max(0, Math.min(1, performance)), n = clubNight(s), t = tonight(s);
  const events = advance(s, 25);
  t.danced++;
  s.needs.fun = clamp(s.needs.fun + 10 + p * 18);
  s.needs.social = clamp(s.needs.social + (n === 'packed' ? 12 : n === 'busy' ? 8 : 3));
  s.needs.energy = clamp(s.needs.energy - 8);
  s.skills.fitness = Math.min(100, s.skills.fitness + 0.25);
  nudge(s, 'outgoing', 0.006, events);
  const lines = [p > 0.8 ? 'You owned that floor. People made room.' : p > 0.5 ? 'You found the beat and stayed on it.' : 'You were a step behind the beat, but nobody here is judging.'];
  let met: string | null = null;
  const chance = n === 'quiet' ? 0 : (0.15 + p * 0.35 + s.skills.social / 400) * (n === 'packed' ? 1.25 : 1) * (t.vip ? 1.3 : 1);
  if (partner && !s.contacts.some((c) => c.name === partner) && new Rng((s.seed ^ Math.imul(s.minutes + 17, 0x9e3779b1)) >>> 0).chance(chance)) {
    met = partner;
    s.contacts.push({ name: partner, relation: 'Acquaintance', closeness: 25, met: s.minutes });
    s.history.push({ t: s.minutes, text: `Met ${partner} on the dance floor at ${place}.` });
    lines.push(`${partner.split(' ')[0]} danced next to you for three songs, then put their number in your phone.`);
  }
  return { lines, events, met };
}

export function vipPrice(s: LifeState): number { return localPrice(s, 350_000, 5_000); }

// --- what a Lagos night out is really about: spraying, the shout-out, the bottle ------------------

/** The bottle you order when you want the whole room to know: a blue-and-white ceramic decanter (a made-up brand). */
export const PREMIUM = { name: 'Celeste Reposado', short: 'Celeste' } as const;
export function premiumPrice(s: LifeState): number { return localPrice(s, 1_800_000, 50_000); }

/**
 * Order the Celeste: it comes to you with sparklers, the bottle girls, your name on a board and the DJ calling it
 * (the game plays that). Comes with the table. Big social status, big money.
 */
export function premiumBottle(s: LifeState, place: string): { ok: boolean; lines: string[]; events: LifeEvent[] } {
  const t = tonight(s);
  const price = premiumPrice(s);
  if (!spend(s, price, `${PREMIUM.short} at ${place}`)) return { ok: false, lines: [`A ${PREMIUM.short} is ${naira(s, price)}. Not tonight.`], events: [] };
  t.vip = true;
  t.bottles = (t.bottles ?? 0) + 1;
  s.stats.bottles = (s.stats.bottles ?? 0) + 1;
  const events = advance(s, 30);
  s.needs.social = clamp(s.needs.social + 35);
  s.needs.fun = clamp(s.needs.fun + 25);
  nudge(s, 'outgoing', 0.015, events);
  const soc = s.social, seen = soc ? Math.round(15 + soc.followers * 0.04) : 0;
  if (soc) soc.followers += seen;
  s.history.push({ t: s.minutes, text: `Ordered a ${PREMIUM.short} at ${place}. The whole club saw.` });
  return { ok: true, lines: [`The ${PREMIUM.short} came out like a coronation.`, 'Phones were up all over the room.', ...(seen ? [`${seen} new followers by morning: half the club posted it.`] : [])], events };
}

/** The bundles you can spray, in notes of this country (a stack of ₦1,000s, of €50s…). */
export function sprayBundles(s: LifeState): number[] { return [50_000, 200_000, 500_000, 1_000_000].map((n) => localPrice(s, n, 1_000)); }

/**
 * You sprayed `amount` (on the floor, the DJ, the dancers, your table). Status: people see it and remember;
 * a big spray gets you a shout-out from the DJ (`shout`), and the floor is a little more yours.
 */
export function sprayed(s: LifeState, amount: number, on: string, place: string): { ok: boolean; lines: string[]; events: LifeEvent[]; shout: boolean } {
  if (amount <= 0 || !spend(s, amount, `Sprayed ${on}`)) return { ok: false, lines: ['You reach for the stack. It isn\'t there.'], events: [], shout: false };
  const t = tonight(s);
  t.sprayed = (t.sprayed ?? 0) + amount;
  s.stats.sprayed = (s.stats.sprayed ?? 0) + amount;
  const k = amount / localPrice(s, 200_000, 1_000); // 1 = a solid spray
  const events = advance(s, 5);
  s.needs.social = clamp(s.needs.social + Math.min(30, 6 + k * 8));
  s.needs.fun = clamp(s.needs.fun + Math.min(25, 5 + k * 6));
  nudge(s, 'outgoing', 0.004 * Math.min(4, k), events);
  const shout = t.sprayed >= localPrice(s, 500_000, 1_000) && !t.shouted;
  if (shout) t.shouted = true;
  const soc = s.social;
  if (soc && k >= 2) soc.followers += Math.round(4 + k * 3);
  if (k >= 2.5) s.history.push({ t: s.minutes, text: `Sprayed ${naira(s, amount)} at ${place}.` });
  return { ok: true, lines: [k >= 2.5 ? 'It rained. People were picking notes out of the air.' : k >= 1 ? 'The notes float down over the floor. The crowd loves it.' : 'A little spray. Someone cheers.'], events, shout };
}

/** A shout-out from the DJ, paid for: your name on the mic and on the screen. */
export function shoutOutPrice(s: LifeState): number { return localPrice(s, 100_000, 5_000); }
export function shoutOut(s: LifeState): { ok: boolean; events: LifeEvent[] } {
  const price = shoutOutPrice(s);
  if (!spend(s, price, 'Shout-out from the DJ')) return { ok: false, events: [] };
  const t = tonight(s);
  t.shouted = true;
  s.needs.social = clamp(s.needs.social + 10);
  s.needs.fun = clamp(s.needs.fun + 6);
  return { ok: true, events: advance(s, 3) };
}

/** Bottle service: a table, a bottle with a sparkler, and everyone looking over. Social status, at a price. */
export function bottleService(s: LifeState, place: string): { ok: boolean; lines: string[]; events: LifeEvent[] } {
  const t = tonight(s);
  if (t.vip) return { ok: false, lines: ['The table\'s yours already.'], events: [] };
  const price = vipPrice(s);
  if (!spend(s, price, 'Bottle service')) return { ok: false, lines: [`A table is ${naira(s, price)}. Not tonight.`], events: [] };
  t.vip = true;
  const events = advance(s, 40);
  s.needs.social = clamp(s.needs.social + 25);
  s.needs.fun = clamp(s.needs.fun + 18);
  nudge(s, 'outgoing', 0.01, events);
  const soc = s.social;
  const seen = soc ? Math.round(5 + soc.followers * 0.02) : 0;
  if (soc) soc.followers += seen;
  s.history.push({ t: s.minutes, text: `Took a table at ${place}.` });
  return { ok: true, lines: ['The bottle comes out with a sparkler and the whole room turns to look.', clubNight(s) === 'packed' ? 'People you\'ve never met come over to say hello.' : 'Half the club wishes they were at your table.', ...(seen ? [`${seen} new followers by the morning: people talk.`] : [])], events };
}

// --- church ---------------------------------------------------------------------------------------

/** Sunday morning service: the gig. Other days the hall is open for rehearsal. */
export function serviceNow(s: LifeState): boolean {
  const h = hourOf(s.minutes);
  return weekday(s.minutes) === 6 && h >= 7.5 && h < 12.5;
}

/**
 * You played: at a Sunday service it's paid (and talked about), on another day it's rehearsal.
 * `performance` is how the playing went (0..1).
 */
export function churchPlay(s: LifeState, performance: number): { lines: string[]; events: LifeEvent[] } {
  const p = Math.max(0, Math.min(1, performance));
  const events: LifeEvent[] = [];
  const lines: string[] = [];
  const service = serviceNow(s);
  const gigsBefore = s.ledger.filter((x) => x.label === 'Church keyboard').length;
  s.skills.music = Math.min(100, s.skills.music + (0.6 + p * 1.6) * (1 - s.skills.music / 140));
  s.needs.fun = clamp(s.needs.fun + 10 + p * 15);
  s.needs.social = clamp(s.needs.social + (service ? 25 : 8));
  if (service) {
    // the choir master pays the keyboardist; a good one gets more and gets asked back
    const base = 6_000 + s.skills.music * 220;
    const pay = Math.round((base * (0.5 + p)) / 500) * 500;
    if (p >= 0.35) {
      earn(s, pay, 'Church keyboard');
      lines.push(p > 0.75 ? `The congregation was on its feet. The choir master pressed ${naira(s, pay)} into your hand: "Same time next week?"` : `It held together. ${naira(s, pay)} from the choir master, with a few notes about the bridge.`);
      if (p > 0.75 && gigsBefore >= 2 && !s.contacts.some((c) => c.relation === 'Choir master')) {
        s.contacts.push({ name: 'Brother Femi', relation: 'Choir master', closeness: 40, met: s.minutes });
        s.inbox.push({ at: s.minutes + 2 * DAY + 10 * 60, from: 'Brother Femi', text: 'A couple in the church is getting married next month. They want you on keys for the reception. Good money.' });
      }
      nudge(s, 'disciplined', 0.01);
    } else {
      lines.push('Wrong chords in the offertory song. The choir master took over the keys for the second half. No pay today.');
    }
    if (!s.history.some((h) => h.text.startsWith('First played at church'))) s.history.push({ t: s.minutes, text: 'First played at church on a Sunday.' });
  } else {
    lines.push(p > 0.7 ? 'Rehearsal went well. The choir master noticed: "Come on Sunday."' : 'Rehearsal. You\'ll need to be tighter than that on Sunday.');
  }
  events.push(...advance(s, service ? 180 : 75), ...progress(s, 'practice'));
  return { lines, events };
}
